import React, { useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import './NewsStack.css';

const normalizeNewsItem = (item, idx) => {
  if (!item || typeof item !== 'object') {
    return { id: `notification-${idx}`, title: '', htmlContent: '', image: '' };
  }
  return {
    id: item.id ?? item.notificationId ?? item.uuid ?? `notification-${idx}`,
    title: item.title ?? item.subject ?? item.name ?? '',
    htmlContent: item.htmlContent ?? item.html ?? item.bodyHtml ?? item.description ?? item.message ?? item.body ?? item.text ?? '',
    image: item.image ?? item.imageUrl ?? ''
  };
};

const toImageSrc = (image) => {
  if (!image || typeof image !== 'string') return '';
  const trimmed = image.trim();
  if (!trimmed) return '';

  // Already a data URL
  if (trimmed.startsWith('data:')) return trimmed;

  // Normal URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;

  // Common JPEG base64 prefix: "/9j/" (as in your example)
  if (trimmed.startsWith('/9j/')) return `data:image/jpeg;base64,${trimmed}`;

  // Fallback: try to treat as a raw base64 blob
  if (/^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 100) return `data:image/jpeg;base64,${trimmed}`;

  // Otherwise, render as-is (in case backend returns a relative path)
  return trimmed;
};

const NewsStack = ({ show = false, onClose = () => {}, items = [] }) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const newsData = useMemo(() => (Array.isArray(items) ? items.map(normalizeNewsItem) : []), [items]);

  useEffect(() => {
    if (show) {
      setIndex(0);
      setVisible(true);
    }
  }, [show]);

  const handleNext = () => {
    if (index < newsData.length - 1) {
      setIndex(index + 1);
    } else {
      setVisible(false);
      setTimeout(() => {
        setIndex(0);
        onClose();
      }, 250);
    }
  };

  if (!visible) return null;
  if (!newsData.length) return null;

  return (
    <div
      className="news-overlay"
      onClick={(e) => {
        if (e.target.classList.contains('news-overlay')) handleNext();
      }}
    >
      <div className="news-stack-container">
        {newsData.slice(index, index + 3).map((news, i) => {
          const stackClass = i === 0 ? 'active' : i === 1 ? 'stack stack-1' : 'stack stack-2';

          return (
            <div key={news.id} className={`news-card ${stackClass}`}>
              {i === 0 && (
                <button className="close-btn" onClick={handleNext}>
                  <FiX size={20} />
                </button>
              )}

              {news.image && (
                <div className="news-img-wrapper">
                  <img
                    src={toImageSrc(news.image)}
                    alt={news.title}
                    className="news-img"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                </div>
              )}

              <h4 className="news-title">{news.title}</h4>

              <div className="news-body" dangerouslySetInnerHTML={{ __html: news.htmlContent }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewsStack;
